/**
 * Shared domain types for API Rescue Desk.
 *
 * This module is deliberately dependency-free so it can run unchanged in the
 * browser (Vite bundle), in a Cloudflare Worker, and in Node-based tests.
 */

/* -------------------------------------------------------------------------- */
/* Input classification                                                       */
/* -------------------------------------------------------------------------- */

export const INPUT_TYPES = [
  'curl',
  'http_request',
  'http_response',
  'json',
  'yaml',
  'webhook',
  'stack_trace',
  'api_error',
  'unknown',
] as const;

export type InputType = (typeof INPUT_TYPES)[number];

/** What the caller may ask for. `auto` keeps server-side detection authoritative. */
export type InputTypeSelection = InputType | 'auto';

export interface DetectionResult {
  type: InputType;
  confidence: number;
  /** Ordered explanations of why this classification was chosen. */
  signals: string[];
  /** Secondary candidates, best first. */
  alternatives: Array<{ type: InputType; confidence: number }>;
}

/* -------------------------------------------------------------------------- */
/* Sanitisation                                                               */
/* -------------------------------------------------------------------------- */

export type RedactionPlaceholder =
  | '[REDACTED_BEARER_TOKEN]'
  | '[REDACTED_BASIC_CREDENTIALS]'
  | '[REDACTED_AUTHORIZATION]'
  | '[REDACTED_API_KEY]'
  | '[REDACTED_JWT]'
  | '[REDACTED_OAUTH_TOKEN]'
  | '[REDACTED_COOKIE]'
  | '[REDACTED_SET_COOKIE]'
  | '[REDACTED_PASSWORD]'
  | '[REDACTED_CLIENT_SECRET]'
  | '[REDACTED_PRIVATE_KEY]'
  | '[REDACTED_CONNECTION_STRING]'
  | '[REDACTED_EMAIL]'
  | '[REDACTED_CREDIT_CARD]'
  | '[REDACTED_SECRET]';

export interface RedactionHit {
  placeholder: RedactionPlaceholder;
  /** Human label for the category of data that was removed. */
  category: string;
  count: number;
}

export interface SanitizationReport {
  redacted: boolean;
  totalRedactions: number;
  hits: RedactionHit[];
  /** Field names the caller asked us to treat as sensitive. */
  customSensitiveFields: string[];
  /** Plain-language caveat; redaction is best-effort, never guaranteed. */
  warning: string;
  notes: string[];
}

/* -------------------------------------------------------------------------- */
/* Parsed evidence                                                            */
/* -------------------------------------------------------------------------- */

export interface ParsedCurl {
  method: string;
  url: string;
  /** Hostname only; used for diagnostics without fetching anything. */
  host: string | null;
  path: string | null;
  headers: Array<{ name: string; value: string }>;
  query: Array<{ name: string; value: string }>;
  body: string | null;
  bodyTruncated: boolean;
  hasBasicAuthIndicator: boolean;
  hasBearerTokenIndicator: boolean;
  formFields: Array<{ name: string; value: string }>;
  /** Unsupported flags we noticed but intentionally ignored. */
  ignoredFlags: string[];
  warnings: string[];
}

export interface ParsedHttpMessage {
  kind: 'request' | 'response';
  startLine: string;
  method?: string;
  target?: string;
  statusCode?: number;
  statusText?: string;
  httpVersion?: string;
  headers: Array<{ name: string; value: string }>;
  body: string | null;
  bodyTruncated: boolean;
  warnings: string[];
}

export interface ParsedPayload {
  format: 'json' | 'yaml' | 'text';
  valid: boolean;
  parseError: string | null;
  topLevelKeys: string[];
  /** `null` when the value is not a JSON/YAML object or array. */
  value: unknown;
  depth: number;
  nodeCount: number;
}

export interface ParsedStackTrace {
  frames: Array<{ raw: string; file: string | null; line: number | null; fn: string | null }>;
  errorType: string | null;
  errorMessage: string | null;
  runtime: 'node' | 'browser' | 'python' | 'java' | 'dotnet' | 'ruby' | 'go' | 'unknown';
}

export interface ParsedWebhook {
  eventName: string | null;
  eventId: string | null;
  signatureHeaders: string[];
  hasSignature: boolean;
  idempotencyKeys: string[];
  timestampFields: string[];
}

export interface ParsedApiError {
  statusCode: number | null;
  errorCode: string | null;
  errorType: string | null;
  message: string | null;
  retryableHint: boolean | null;
}

export interface ParsedEvidence {
  inputType: InputType;
  curl?: ParsedCurl;
  http?: ParsedHttpMessage;
  /**
   * Present when the submission contained both a request and its response
   * (for example a pasted request/response transcript).
   */
  httpResponse?: ParsedHttpMessage;
  payload?: ParsedPayload;
  stackTrace?: ParsedStackTrace;
  webhook?: ParsedWebhook;
  apiError?: ParsedApiError;
}

/* -------------------------------------------------------------------------- */
/* Findings                                                                   */
/* -------------------------------------------------------------------------- */

/** Confirmed = deterministic evidence. Likely = strong heuristic. Speculative = a hint only. */
export type FindingKind = 'confirmed' | 'likely' | 'speculative';

export type Severity = 'error' | 'warning' | 'info';

export interface DeterministicFinding {
  code: string;
  severity: Severity;
  kind: FindingKind;
  title: string;
  detail: string;
  evidence: string[];
  category: string;
  /** Suggested follow-ups, phrased as instructions rather than facts. */
  suggestions: string[];
  /** Only set by OpenAPI validation, where the spec is the source of truth. */
  source?: 'deterministic' | 'openapi';
}

/* -------------------------------------------------------------------------- */
/* AI provider contract                                                       */
/* -------------------------------------------------------------------------- */

export interface ProviderCause {
  title: string;
  confidence: number;
  kind: FindingKind;
  category: string;
  explanation: string;
  evidence: string[];
}

export interface ProviderStep {
  title: string;
  detail: string;
  whyItMatters: string;
}

export interface ProviderCorrectedExample {
  format: 'curl' | 'http' | 'json' | 'yaml' | 'text';
  title: string;
  content: string;
  notes: string[];
}

export interface ProviderAnalysis {
  executiveSummary: string;
  likelyCauses: ProviderCause[];
  troubleshootingSteps: ProviderStep[];
  correctedExample: ProviderCorrectedExample | null;
  verificationChecklist: string[];
  informationStillNeeded: string[];
  uncertaintyNotes: string[];
}

export interface ProviderRequest {
  inputType: InputType;
  sanitizedInput: string;
  evidence: ParsedEvidence;
  deterministicFindings: DeterministicFinding[];
  openApiContext: string | null;
  customSensitiveFields: string[];
}

export interface ProviderResult {
  analysis: ProviderAnalysis;
  providerName: string;
  model: string | null;
  latencyMs: number;
}

export interface AiProvider {
  readonly name: string;
  readonly model: string | null;
  analyse(input: ProviderRequest, signal?: AbortSignal): Promise<ProviderResult>;
}

/* -------------------------------------------------------------------------- */
/* OpenAPI                                                                    */
/* -------------------------------------------------------------------------- */

export interface OpenApiValidationFinding {
  code: string;
  severity: Severity;
  kind: FindingKind;
  message: string;
  /** JSON pointer-ish location, e.g. `request.body.customer.email`. */
  location: string;
}

export interface OpenApiValidationReport {
  available: boolean;
  /** `structural` for Free, `full` for Pro. */
  depth: 'structural' | 'full' | 'none';
  reason: string | null;
  specId: string | null;
  specName: string | null;
  matchedOperation: {
    method: string;
    path: string;
    operationId: string | null;
  } | null;
  requestedMethod: string | null;
  requestedPath: string | null;
  checksPerformed: string[];
  checksSkipped: string[];
  findings: OpenApiValidationFinding[];
}

/* -------------------------------------------------------------------------- */
/* Report                                                                     */
/* -------------------------------------------------------------------------- */

export interface LikelyCause {
  id: string;
  title: string;
  confidence: number;
  kind: FindingKind;
  category: string;
  explanation: string;
  evidence: string[];
  source: 'deterministic' | 'openapi' | 'model';
}

export interface TroubleshootingStep {
  order: number;
  title: string;
  detail: string;
  whyItMatters: string;
}

export interface CorrectedExample {
  format: 'curl' | 'http' | 'json' | 'yaml' | 'text';
  title: string;
  content: string;
  notes: string[];
  generatedBy: 'deterministic' | 'model';
}

export interface DiagnosisReport {
  version: 1;
  detectedInputType: InputType;
  inputTypeSource: 'auto' | 'manual';
  generatedAt: string;
  executiveSummary: string;
  mostLikelyRootCause: LikelyCause | null;
  likelyCauses: LikelyCause[];
  deterministicFindings: DeterministicFinding[];
  openApiValidation: OpenApiValidationReport;
  troubleshootingSteps: TroubleshootingStep[];
  correctedExample: CorrectedExample | null;
  verificationChecklist: string[];
  informationStillNeeded: string[];
  supportTicketSummary: string;
  uncertaintyNotes: string[];
  safetyNotes: string[];
  limitations: string[];
  sanitization: SanitizationReport;
  provider: {
    name: string;
    model: string | null;
    mode: 'user_key' | 'platform' | 'mock' | 'none';
    usedAi: boolean;
    latencyMs: number | null;
  };
  counts: {
    confirmed: number;
    likely: number;
    speculative: number;
  };
}

/* -------------------------------------------------------------------------- */
/* Plans, entitlements and quotas                                             */
/* -------------------------------------------------------------------------- */

export type PlanId = 'free' | 'pro';

export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused'
  | 'none';

export interface Entitlements {
  planId: PlanId;
  displayName: string;
  priceMonthlyUsd: number | null;
  monthlyDiagnoses: number;
  maxInputBytes: number;
  maxSavedSpecs: number;
  /** `null` means history never expires (while the account exists). */
  historyRetentionDays: number | null;
  persistentHistory: boolean;
  openApiValidation: 'none' | 'structural' | 'full';
  shareableReports: boolean;
  exportFormats: Array<'markdown' | 'json'>;
  priorityProcessing: boolean;
}

export interface QuotaStatus {
  planId: PlanId;
  limit: number;
  used: number;
  remaining: number;
  periodStart: string;
  periodEnd: string;
  /** True when the subscription grants Pro but Stripe says it is not usable. */
  paymentIssue: boolean;
}

export interface SubscriptionSummary {
  planId: PlanId;
  status: SubscriptionStatus;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** True when the Stripe identifiers exist server-side; never the values. */
  linkedToStripe: boolean;
}

export interface ProfileSummary {
  id: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  createdAt: string;
}

export interface MeResponse {
  profile: ProfileSummary;
  subscription: SubscriptionSummary;
  quota: QuotaStatus;
  entitlements: Entitlements;
  billing: {
    available: boolean;
    reason: string | null;
  };
  providers: {
    platformAiEnabled: boolean;
    mockAllowed: boolean;
  };
}

/* -------------------------------------------------------------------------- */
/* Persistence shapes                                                         */
/* -------------------------------------------------------------------------- */

export type DiagnosisStatus = 'pending' | 'complete' | 'failed';

export interface DiagnosisRecord {
  id: string;
  userId: string;
  openApiSpecId: string | null;
  detectedInputType: InputType;
  sanitizedInput: string;
  diagnosisSummary: string;
  likelyCauses: LikelyCause[];
  troubleshootingSteps: TroubleshootingStep[];
  correctedExample: string | null;
  report: DiagnosisReport;
  providerName: string;
  status: DiagnosisStatus;
  expiresAt: string | null;
  idempotencyKey: string;
  createdAt: string;
  updatedAt: string;
}

export interface OpenApiSpecRecord {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  sourceFormat: 'json' | 'yaml';
  normalizedSpec: unknown;
  contentHash: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShareLinkRecord {
  id: string;
  diagnosisId: string;
  userId: string;
  tokenHash: string;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
}

/* -------------------------------------------------------------------------- */
/* API envelopes                                                              */
/* -------------------------------------------------------------------------- */

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    /** Safe, non-sensitive guidance. Never contains user input or secrets. */
    hint?: string;
    details?: Record<string, unknown>;
    requestId: string;
  };
}

export interface DiagnosisResponseBody {
  diagnosis: {
    id: string;
    createdAt: string;
    detectedInputType: InputType;
    status: DiagnosisStatus;
    expiresAt: string | null;
    report: DiagnosisReport;
    sanitizedInput: string;
  };
  quota: QuotaStatus;
  /** True when an idempotency key replays an earlier successful request. */
  replayed: boolean;
}

export interface SharedReportResponseBody {
  report: DiagnosisReport;
  sanitizedInput: string;
  createdAt: string;
  sharedBy: string;
  expiresAt: string;
}
